export const fieldRequiredError = (value: string) => {
  if (value === "") {
    return "This field is required";
  }
  return undefined;
};

const isAustralia = (country: string | null | undefined): boolean => {
  const lowerCaseCountry = country?.toLowerCase();
  return lowerCaseCountry === "australia" || lowerCaseCountry === "au";
};

const AUSTRALIA_STREET_MAX_LENGTH = 40;
const STREET1_MIN_LENGTH = 3;

interface AustraliaStreetValidationResult {
  street1Error: string | undefined;
  street2Error: string | undefined;
  hasBlockingError: boolean;
}

const validateAustraliaStreets = (
  street1: string | null | undefined,
  street2: string | null | undefined,
): AustraliaStreetValidationResult => {
  const street1Length = (street1 ?? "").length;
  const street2Length = (street2 ?? "").length;

  const street1Error =
    street1Length > AUSTRALIA_STREET_MAX_LENGTH
      ? "Must be less than 40 characters. Use additional field below if needed."
      : undefined;
  const street2Error =
    street2Length > AUSTRALIA_STREET_MAX_LENGTH
      ? "Must be less than 40 characters."
      : undefined;

  return {
    street1Error,
    street2Error,
    hasBlockingError: street1Error !== undefined || street2Error !== undefined,
  };
};

export enum AddressValidationErrorType {
  AustraliaStreetLength = "australia_street_length",
  Street1MinLength = "street1_min_length",
}

export interface BlockingAddressError {
  type: AddressValidationErrorType;
  message: string;
  addressType?: string;
}

const checkStreet1MinLengthError = (
  street1?: string | null,
  street2?: string | null,
  addressType?: string,
): BlockingAddressError | null => {
  const street1Trimmed = (street1 ?? "").trim();
  const street2Trimmed = (street2 ?? "").trim();
  const street1Length = street1Trimmed.length;
  const hasStreet2 = street2Trimmed.length > 0;

  if (street1Length > 0 && street1Length < STREET1_MIN_LENGTH) {
    if (hasStreet2) {
      return {
        type: AddressValidationErrorType.Street1MinLength,
        message:
          "Street address line 1 must be at least 3 characters. It looks like your address might be split across two lines, consider combining them into street address line 1.",
        addressType,
      };
    }
    return {
      type: AddressValidationErrorType.Street1MinLength,
      message: "Street address line 1 must be at least 3 characters.",
      addressType,
    };
  }

  return null;
};

const checkAustraliaStreetLengthError = (
  street1?: string | null,
  street2?: string | null,
  country?: string | null,
  addressType?: string,
): BlockingAddressError | null => {
  if (!isAustralia(country)) {
    return null;
  }

  const validation = validateAustraliaStreets(street1, street2);

  if (!validation.hasBlockingError) {
    return null;
  }

  if (validation.street1Error) {
    return {
      type: AddressValidationErrorType.AustraliaStreetLength,
      message:
        "Street address line 1 must be 40 characters or less. Use street address line 2 if needed.",
      addressType,
    };
  }

  if (validation.street2Error) {
    return {
      type: AddressValidationErrorType.AustraliaStreetLength,
      message: "Street address line 2 must be 40 characters or less.",
      addressType,
    };
  }

  return null;
};

export const checkBlockingAddressErrors = (
  street1?: string | null,
  street2?: string | null,
  country?: string | null,
  addressType?: string,
): BlockingAddressError | null => {
  // Check each type of blocking error in order
  // Return the first error found

  const street1MinLengthError = checkStreet1MinLengthError(
    street1,
    street2,
    addressType,
  );
  if (street1MinLengthError) {
    return street1MinLengthError;
  }

  const australiaStreetLengthError = checkAustraliaStreetLengthError(
    street1,
    street2,
    country,
    addressType,
  );
  if (australiaStreetLengthError) {
    return australiaStreetLengthError;
  }

  return null;
};

export const getAustraliaStreetValidationErrors = (
  street1?: string | null,
  street2?: string | null,
  country?: string | null,
): { street1: string | undefined; street2: string | undefined } => {
  if (!isAustralia(country)) {
    return { street1: undefined, street2: undefined };
  }

  const validation = validateAustraliaStreets(street1, street2);
  return {
    street1: validation.street1Error,
    street2: validation.street2Error,
  };
};
